import {
  ChangeEvent,
  ClipboardEvent as ReactClipboardEvent,
  CSSProperties,
  FC,
  Fragment,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {URL as BACKEND_URL, prependBackendUrl} from '../../gql/client'
import {toast} from 'react-toastify'
import {
  PolicyChatAssignmentGroup,
  MuteDuration,
  PolicyChatAttachment,
  PolicyChatMessage,
  PolicyChatMessageReaction,
  PolicyChatThread,
  PolicyChatUser,
  usePolicyChat,
} from './hooks/usePolicyChat'
import './PolicyChatPage.css'

const QUICK_EMOJIS = [
  '\u{1F600}',
  '\u{1F44D}',
  '\u{1F525}',
  '\u{1F3AF}',
  '\u{1F44F}',
  '\u{1F64F}',
]

const QUICK_MESSAGE_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏']

const FILTER_ORDER = ['all', 'unread', 'favorites', 'groups', 'archived'] as const

type SidebarFilter = (typeof FILTER_ORDER)[number]
type DraftPreview = {
  file: File
  key: string
  url: string | null
}
type SidebarGroupEntry =
  | {kind: 'thread'; thread: PolicyChatThread}
  | {kind: 'assignment-group'; group: PolicyChatAssignmentGroup}

const AVATAR_COLORS = [
  '#1a73e8',
  '#7e57c2',
  '#26a69a',
  '#ef5350',
  '#ff7043',
  '#66bb6a',
  '#5c6bc0',
  '#ec407a',
  '#ab47bc',
  '#42a5f5',
]

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

const getColorForKey = (key: string | number) => {
  const text = String(key)
  let hash = 0
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

type UserAvatarProps = {
  name: string
  avatar?: string | null
  size?: number
  colorKey?: string | number
  groupIcon?: boolean
}

const UserAvatar: FC<UserAvatarProps> = ({name, avatar, size = 44, colorKey, groupIcon}) => {
  const dimension = `${size}px`
  const baseStyle: CSSProperties = {
    width: dimension,
    height: dimension,
    borderRadius: '50%',
    flexShrink: 0,
    objectFit: 'cover',
  }

  if (avatar) {
    return <img src={prependBackendUrl(avatar)} alt={name} style={baseStyle} />
  }

  const background = groupIcon ? '#90a4ae' : getColorForKey(colorKey ?? name)
  return (
    <div
      style={{
        ...baseStyle,
        backgroundColor: background,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: `${Math.max(12, size * 0.4)}px`,
        textTransform: 'uppercase',
        userSelect: 'none',
      }}
    >
      {groupIcon ? <i className='bi bi-people-fill text-white' /> : getInitials(name)}
    </div>
  )
}

const isMessageReadByOthers = (message: PolicyChatMessage, thread: PolicyChatThread | null) => {
  if (!thread || !message.is_own) return false
  const others = thread.participants.filter((participant) => !participant.is_self)
  if (others.length === 0) return false
  const sentAt = new Date(message.created_at).getTime()
  return others.every((participant) => {
    if (!participant.last_read_at) return false
    return new Date(participant.last_read_at).getTime() >= sentAt
  })
}

const isLastMessageOwn = (thread: PolicyChatThread) => {
  const lastMessage = thread.last_message
  if (!lastMessage || !lastMessage.sender) return false
  const self = thread.participants.find((participant) => participant.is_self)
  if (!self) return false
  return lastMessage.sender.id === self.user.id
}

const isLastMessageRead = (thread: PolicyChatThread) => {
  const lastMessage = thread.last_message
  if (!lastMessage) return false
  const others = thread.participants.filter((participant) => !participant.is_self)
  if (others.length === 0) return false
  const sentAt = new Date(lastMessage.created_at).getTime()
  return others.every((participant) => {
    if (!participant.last_read_at) return false
    return new Date(participant.last_read_at).getTime() >= sentAt
  })
}

type ReadTicksProps = {
  read: boolean
  ownBubble: boolean
}

const ReadTicks: FC<ReadTicksProps> = ({read, ownBubble}) => {
  const color = read ? '#34b7f1' : ownBubble ? 'rgba(255, 255, 255, 0.85)' : '#9aa5b1'
  return (
    <i
      className='bi bi-check2-all ms-1'
      style={{color, fontSize: '1rem', lineHeight: 1}}
      title={read ? 'Read' : 'Sent'}
    />
  )
}

const formatDateTime = (value?: string | null) => {
  if (!value) return ''
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))
}

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

const formatDayLabel = (value: string) => {
  const date = new Date(value)
  const today = startOfDay(new Date())
  const target = startOfDay(date)
  const diffDays = Math.round((today - target) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays > 0 && diffDays < 7) {
    return new Intl.DateTimeFormat('en-US', {weekday: 'long'}).format(date)
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: target < startOfDay(new Date(new Date().getFullYear(), 0, 1)) ? 'numeric' : undefined,
  }).format(date)
}

type ThreadAvatarTarget = {
  name: string
  avatar?: string | null
  colorKey: string | number
  groupIcon: boolean
}

const getThreadAvatarTarget = (thread: PolicyChatThread): ThreadAvatarTarget => {
  if (thread.thread_type === 'GROUP') {
    return {
      name: thread.display_name,
      avatar: null,
      colorKey: `group-${thread.id}`,
      groupIcon: true,
    }
  }
  const other = thread.participants.find((participant) => !participant.is_self)?.user
  return {
    name: other?.full_name || thread.display_name,
    avatar: other?.avatar || null,
    colorKey: other?.id ?? thread.id,
    groupIcon: false,
  }
}

const getThreadSnippet = (thread: PolicyChatThread) => {
  const lastMessage = thread.last_message
  if (!lastMessage) return 'No messages yet'
  if (lastMessage.text?.trim()) return lastMessage.text
  if (lastMessage.attachments?.length) {
    const firstAttachment = lastMessage.attachments[0]
    if (firstAttachment.attachment_type === 'AUDIO') return 'Voice message'
    if (firstAttachment.attachment_type === 'IMAGE') return 'Photo'
    if (firstAttachment.attachment_type === 'VIDEO') return 'Video'
    return `Attachment: ${firstAttachment.original_name}`
  }
  return lastMessage.message_type
}

const getAssignmentGroupSnippet = (group: PolicyChatAssignmentGroup) => {
  if (group.participant_preview.length > 0) {
    return group.participant_preview.join(', ')
  }
  if (group.participant_count > 0) {
    return `${group.participant_count} members ready`
  }
  return 'Start the group conversation'
}

const getDraftFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`

const getAttachmentLabel = (attachmentType: PolicyChatAttachment['attachment_type']) => {
  if (attachmentType === 'AUDIO') return 'Voice message'
  if (attachmentType === 'IMAGE') return 'Photo'
  if (attachmentType === 'VIDEO') return 'Video'
  return 'Attachment'
}

const getReactionTooltip = (reaction: PolicyChatMessageReaction) => {
  if (reaction.user_names.length === 0) return reaction.emoji
  if (reaction.user_names.length <= 3) return reaction.user_names.join(', ')
  return `${reaction.user_names.slice(0, 3).join(', ')} and ${reaction.user_names.length - 3} more`
}

const getDraftLabel = (file: File) => {
  if (file.type.startsWith('audio/')) return 'Voice message'
  if (file.type.startsWith('image/')) return 'Photo'
  if (file.type.startsWith('video/')) return 'Video'
  return file.name
}

const formatRecordingTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

const canPreviewDraft = (file: File) =>
  file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/')

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const renderHighlightedText = (text: string, query: string) => {
  const normalizedQuery = query.trim()
  if (!normalizedQuery) return text

  const pattern = new RegExp(`(${escapeRegExp(normalizedQuery)})`, 'ig')
  const parts = text.split(pattern)
  if (parts.length === 1) return text

  return parts.map((part, index) =>
    part.toLowerCase() === normalizedQuery.toLowerCase() ? (
      <mark key={`${part}-${index}`} className='policychat-text-highlight'>
        {part}
      </mark>
    ) : (
      part
    )
  )
}

const formatFileSize = (sizeBytes: number) => {
  if (sizeBytes < 1024) return `${sizeBytes} B`
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`
  if (sizeBytes < 1024 * 1024 * 1024) return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(sizeBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const buildAttachmentUrl = (attachment: PolicyChatAttachment, token: string, inline = true) => {
  const relativeUrl = inline ? attachment.inline_url : attachment.download_url
  const separator = relativeUrl.includes('?') ? '&' : '?'
  return `${BACKEND_URL}${relativeUrl}${separator}token=${encodeURIComponent(token)}`
}

const renderAttachment = (
  attachment: PolicyChatAttachment,
  token: string,
  ownBubble = false,
  onImageClick?: (attachment: PolicyChatAttachment, inlineUrl: string, downloadUrl: string) => void
) => {
  const inlineUrl = buildAttachmentUrl(attachment, token, true)
  const downloadUrl = buildAttachmentUrl(attachment, token, false)

  if (attachment.attachment_type === 'IMAGE') {
    return (
      <button
        key={attachment.id}
        type='button'
        className='border-0 bg-transparent p-0 mt-2 d-block'
        onClick={() => onImageClick?.(attachment, inlineUrl, downloadUrl)}
        style={{cursor: 'zoom-in'}}
      >
        <img
          src={inlineUrl}
          alt={attachment.original_name}
          className='rounded'
          style={{maxWidth: '240px', width: '100%', objectFit: 'cover'}}
        />
      </button>
    )
  }

  if (attachment.attachment_type === 'VIDEO') {
    return (
      <video
        key={attachment.id}
        src={inlineUrl}
        controls
        className='rounded mt-2 w-100'
        style={{maxWidth: '320px'}}
      />
    )
  }

  if (attachment.attachment_type === 'AUDIO') {
    return (
      <div key={attachment.id} className='mt-2'>
        <VoiceNotePlayer
          src={inlineUrl}
          accentClassName={ownBubble ? 'policychat-voice-accent--own' : 'policychat-voice-accent--other'}
        />
      </div>
    )
  }

  return (
    <a
      key={attachment.id}
      href={downloadUrl}
      target='_blank'
      rel='noreferrer'
      className='d-inline-flex align-items-center gap-2 rounded border px-3 py-2 mt-2 text-gray-800 fw-semibold'
    >
      <i className='bi bi-paperclip fs-5' />
      <span>{attachment.original_name}</span>
    </a>
  )
}

const renderDraftPreview = (preview: DraftPreview) => {
  const {file, key, url} = preview

  if (file.type.startsWith('image/') && url) {
    return (
      <div key={key} className='policychat-draft-card'>
        <img src={url} alt={file.name} className='policychat-draft-image' />
        <div className='policychat-draft-meta'>
          <span className='policychat-draft-title'>{getDraftLabel(file)}</span>
          <span className='policychat-draft-subtitle'>{file.name}</span>
        </div>
      </div>
    )
  }

  if (file.type.startsWith('video/') && url) {
    return (
      <div key={key} className='policychat-draft-card'>
        <video src={url} className='policychat-draft-image' muted />
        <div className='policychat-draft-meta'>
          <span className='policychat-draft-title'>{getDraftLabel(file)}</span>
          <span className='policychat-draft-subtitle'>{file.name}</span>
        </div>
      </div>
    )
  }

  if (file.type.startsWith('audio/') && url) {
    return (
      <div key={key} className='policychat-draft-audio'>
        <div className='policychat-draft-audio-icon'>
          <i className='bi bi-mic-fill' />
        </div>
        <div className='policychat-draft-meta flex-grow-1'>
          <span className='policychat-draft-title'>{getDraftLabel(file)}</span>
          <div className='mt-2'>
            <VoiceNotePlayer src={url} accentClassName='policychat-voice-accent--other' />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div key={key} className='policychat-draft-file'>
      <div className='policychat-draft-file-icon'>
        <i className='bi bi-paperclip' />
      </div>
      <div className='policychat-draft-meta'>
        <span className='policychat-draft-title'>{getDraftLabel(file)}</span>
        <span className='policychat-draft-subtitle'>{file.name}</span>
      </div>
    </div>
  )
}

type VoiceNotePlayerProps = {
  accentClassName: string
  src: string
}

const VoiceNotePlayer: FC<VoiceNotePlayerProps> = ({accentClassName, src}) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)

  const waveformBars = useMemo(
    () => [18, 32, 22, 28, 16, 26, 34, 19, 24, 31, 15, 29, 23, 36, 20, 30, 17, 27, 33, 22],
    []
  )

  const togglePlayback = () => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      void audio.play()
      setIsPlaying(true)
      return
    }
    audio.pause()
    setIsPlaying(false)
  }

  const handleSeek = (event: ReactMouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
    audio.currentTime = duration * ratio
    setCurrentTime(audio.currentTime)
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const remainingLabel = formatRecordingTime(Math.max(0, Math.round(duration - currentTime)))

  return (
    <div className='policychat-voice-note'>
      <audio
        ref={audioRef}
        src={src}
        preload='metadata'
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onEnded={() => {
          setIsPlaying(false)
          setCurrentTime(0)
        }}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />
      <button
        type='button'
        className={`policychat-voice-play ${accentClassName}`}
        onClick={togglePlayback}
      >
        <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'} fs-3`} />
      </button>
      <div className='policychat-voice-track' onClick={handleSeek}>
        <div className='policychat-voice-waveform'>
          {waveformBars.map((height, index) => (
            <span
              key={`${height}-${index}`}
              className='policychat-voice-bar'
              style={{
                height: `${height}px`,
                opacity: progress >= ((index + 1) / waveformBars.length) * 100 ? 1 : 0.42,
              }}
            />
          ))}
        </div>
        <div className='policychat-voice-progress'>
          <div className={`policychat-voice-progress-fill ${accentClassName}`} style={{width: `${progress}%`}} />
        </div>
      </div>
      <span className='policychat-voice-duration'>{remainingLabel}</span>
    </div>
  )
}

type MessageActionMenuProps = {
  canCopy: boolean
  canDownload: boolean
  onClose: () => void
  onCopy: () => void
  onDownload: () => void
  onReact: (emoji: string) => void
  onReply: () => void
  reactions: PolicyChatMessageReaction[]
  x: number
  y: number
}

const MessageActionMenu: FC<MessageActionMenuProps> = ({
  canCopy,
  canDownload,
  onClose,
  onCopy,
  onDownload,
  onReact,
  onReply,
  reactions,
  x,
  y,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  return (
    <div ref={ref} className='policychat-message-menu' style={{top: y, left: x}}>
      <div className='policychat-message-reaction-picker'>
        {QUICK_MESSAGE_REACTIONS.map((emoji) => {
          const isActive = reactions.some(
            (reaction) => reaction.emoji === emoji && reaction.reacted_by_self
          )
          return (
            <button
              key={emoji}
              type='button'
              className={`policychat-message-reaction-button ${
                isActive ? 'policychat-message-reaction-button--active' : ''
              }`}
              onClick={() => onReact(emoji)}
              title={`React with ${emoji}`}
            >
              {emoji}
            </button>
          )
        })}
      </div>
      <button type='button' className='policychat-message-menu-item' onClick={onReply}>
        <i className='bi bi-reply fs-6' />
        <span>Reply</span>
      </button>
      {canCopy && (
        <button type='button' className='policychat-message-menu-item' onClick={onCopy}>
          <i className='bi bi-copy fs-6' />
          <span>Copy text</span>
        </button>
      )}
      {canDownload && (
        <button type='button' className='policychat-message-menu-item' onClick={onDownload}>
          <i className='bi bi-download fs-6' />
          <span>Open attachment</span>
        </button>
      )}
    </div>
  )
}

type ImagePreviewModalProps = {
  inlineUrl: string
  downloadUrl: string
  name: string
  onClose: () => void
}

const ImagePreviewModal: FC<ImagePreviewModalProps> = ({inlineUrl, downloadUrl, name, onClose}) => {
  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className='modal d-block'
      style={{backgroundColor: 'rgba(0, 0, 0, 0.85)'}}
      onClick={onClose}
    >
      <div
        className='modal-dialog modal-dialog-centered modal-xl'
        onClick={(event) => event.stopPropagation()}
      >
        <div className='modal-content bg-transparent border-0 shadow-none'>
          <div className='d-flex align-items-center justify-content-end gap-2 mb-3'>
            <a
              href={downloadUrl}
              target='_blank'
              rel='noreferrer'
              className='btn btn-sm btn-light'
              title='Download'
            >
              <i className='bi bi-download me-1' />
              Download
            </a>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={onClose}
              title='Close (Esc)'
            >
              <i className='bi bi-x-lg' />
            </button>
          </div>
          <img
            src={inlineUrl}
            alt={name}
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              margin: '0 auto',
              display: 'block',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>
    </div>
  )
}

type UserPickerModalProps = {
  loading: boolean
  onClose: () => void
  onCreateDirect: (userId: number) => Promise<unknown>
  onQueryChange: (value: string) => void
  query: string
  users: PolicyChatUser[]
}

const UserPickerModal: FC<UserPickerModalProps> = ({
  loading,
  onClose,
  onCreateDirect,
  onQueryChange,
  query,
  users,
}) => (
  <div className='modal d-block' style={{backgroundColor: 'rgba(0, 0, 0, 0.45)'}}>
    <div className='modal-dialog modal-dialog-centered'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='modal-title'>New direct chat</h3>
          <button type='button' className='btn btn-sm btn-icon btn-light' onClick={onClose}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='modal-body'>
          <input
            type='text'
            className='form-control mb-4'
            placeholder='Search by name or email'
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
          />
          <div style={{maxHeight: '320px', overflowY: 'auto'}}>
            {loading && <div className='text-muted'>Loading users...</div>}
            {!loading && users.length === 0 && <div className='text-muted'>No users available.</div>}
            {!loading &&
              users.map((candidate) => (
                <button
                  key={candidate.id}
                  type='button'
                  className='btn btn-light d-flex justify-content-between align-items-center w-100 mb-2'
                  onClick={() => void onCreateDirect(candidate.id)}
                >
                  <span className='d-flex align-items-center gap-3 text-start'>
                    <UserAvatar
                      name={candidate.full_name}
                      avatar={candidate.avatar}
                      colorKey={candidate.id}
                      size={36}
                    />
                    <span>
                      <span className='d-block fw-bold'>{candidate.full_name}</span>
                      <span className='d-block text-muted fs-8'>{candidate.email}</span>
                    </span>
                  </span>
                  <i className='bi bi-chat-dots fs-3' />
                </button>
              ))}
          </div>
        </div>
      </div>
    </div>
  </div>
)

type GroupModalProps = {
  loading: boolean
  onClose: () => void
  onCreateGroup: () => Promise<void>
  onQueryChange: (value: string) => void
  onToggleUser: (userId: number) => void
  query: string
  selectedUserIds: number[]
  setTitle: (value: string) => void
  title: string
  users: PolicyChatUser[]
}

const GroupModal: FC<GroupModalProps> = ({
  loading,
  onClose,
  onCreateGroup,
  onQueryChange,
  onToggleUser,
  query,
  selectedUserIds,
  setTitle,
  title,
  users,
}) => (
  <div className='modal d-block' style={{backgroundColor: 'rgba(0, 0, 0, 0.45)'}}>
    <div className='modal-dialog modal-dialog-centered'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='modal-title'>New group</h3>
          <button type='button' className='btn btn-sm btn-icon btn-light' onClick={onClose}>
            <i className='bi bi-x-lg' />
          </button>
        </div>
        <div className='modal-body'>
          <input
            type='text'
            className='form-control mb-4'
            placeholder='Group name'
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <input
            type='text'
            className='form-control mb-4'
            placeholder='Search users'
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
          />
          <div style={{maxHeight: '280px', overflowY: 'auto'}}>
            {loading && <div className='text-muted'>Loading users...</div>}
            {!loading &&
              users.map((candidate) => {
                const isSelected = selectedUserIds.includes(candidate.id)
                return (
                  <label
                    key={candidate.id}
                    className='d-flex align-items-center justify-content-between rounded border px-3 py-2 mb-2'
                  >
                    <span className='d-flex align-items-center gap-3'>
                      <UserAvatar
                        name={candidate.full_name}
                        avatar={candidate.avatar}
                        colorKey={candidate.id}
                        size={36}
                      />
                      <span>
                        <span className='d-block fw-bold'>{candidate.full_name}</span>
                        <span className='d-block text-muted fs-8'>{candidate.email}</span>
                      </span>
                    </span>
                    <input
                      type='checkbox'
                      checked={isSelected}
                      onChange={() => onToggleUser(candidate.id)}
                    />
                  </label>
                )
              })}
          </div>
        </div>
        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Cancel
          </button>
          <button type='button' className='btn btn-primary' onClick={() => void onCreateGroup()}>
            Create group
          </button>
        </div>
      </div>
    </div>
  </div>
)

type ThreadContextMenuProps = {
  thread: PolicyChatThread
  x: number
  y: number
  onClose: () => void
  onArchive: () => void
  onMute: (duration: MuteDuration) => void
  onPin: () => void
  onLabel: () => void
  onMarkUnread: () => void
  onFavorite: () => void
  onBlock: () => void
  onClear: () => void
  onDelete: () => void
}

const ThreadContextMenu: FC<ThreadContextMenuProps> = ({
  thread,
  x,
  y,
  onClose,
  onArchive,
  onMute,
  onPin,
  onLabel,
  onMarkUnread,
  onFavorite,
  onBlock,
  onClear,
  onDelete,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [muteOpen, setMuteOpen] = useState(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  const blocker = thread.thread_type === 'DIRECT'
  const muteActive = thread.muted || Boolean(thread.mute_until)

  const items: Array<{
    icon: string
    label: string
    danger?: boolean
    onClick?: () => void
    submenu?: boolean
    hidden?: boolean
  }> = [
    {
      icon: 'bi-archive',
      label: thread.is_archived ? 'Unarchive chat' : 'Archive chat',
      onClick: onArchive,
    },
    {
      icon: muteActive ? 'bi-bell' : 'bi-bell-slash',
      label: muteActive ? 'Unmute notifications' : 'Mute notifications',
      submenu: true,
    },
    {
      icon: 'bi-pin-angle',
      label: thread.is_pinned ? 'Unpin chat' : 'Pin chat',
      onClick: onPin,
    },
    {icon: 'bi-tag', label: 'Label chat', onClick: onLabel},
    {icon: 'bi-envelope', label: 'Mark as unread', onClick: onMarkUnread},
    {
      icon: thread.is_favorite ? 'bi-heart-fill' : 'bi-heart',
      label: thread.is_favorite ? 'Remove from Favorites' : 'Add to Favorites',
      onClick: onFavorite,
    },
    {
      icon: 'bi-slash-circle',
      label: 'Block',
      onClick: onBlock,
      hidden: !blocker,
    },
    {icon: 'bi-eraser', label: 'Clear chat', onClick: onClear, danger: true},
    {icon: 'bi-trash', label: 'Delete chat', onClick: onDelete, danger: true},
  ]

  const menuStyle: CSSProperties = {
    position: 'fixed',
    top: y,
    left: x,
    zIndex: 1080,
    minWidth: '240px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    padding: '8px 0',
  }

  return (
    <div ref={ref} style={menuStyle}>
      {items
        .filter((item) => !item.hidden)
        .map((item) =>
          item.submenu ? (
            <div
              key={item.label}
              className='position-relative'
              onMouseEnter={() => setMuteOpen(true)}
              onMouseLeave={() => setMuteOpen(false)}
            >
              <button
                type='button'
                className='btn btn-sm w-100 text-start d-flex align-items-center justify-content-between px-4 py-2 border-0 bg-transparent'
                style={{borderRadius: 0}}
              >
                <span className='d-flex align-items-center gap-3'>
                  <i className={`bi ${item.icon} fs-5`} />
                  <span>{item.label}</span>
                </span>
                <i className='bi bi-chevron-right fs-7 text-muted' />
              </button>
              {muteOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '100%',
                    minWidth: '160px',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                    padding: '8px 0',
                  }}
                >
                  {(muteActive
                    ? ([
                        {label: 'Unmute', value: 'off' as MuteDuration},
                      ] as const)
                    : ([
                        {label: '1 hour', value: '1h' as MuteDuration},
                        {label: '8 hours', value: '8h' as MuteDuration},
                        {label: '1 week', value: '1w' as MuteDuration},
                        {label: 'Always', value: 'always' as MuteDuration},
                      ] as const)
                  ).map((option) => (
                    <button
                      key={option.value}
                      type='button'
                      className='btn btn-sm w-100 text-start px-4 py-2 border-0 bg-transparent'
                      style={{borderRadius: 0}}
                      onClick={() => onMute(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              key={item.label}
              type='button'
              className={`btn btn-sm w-100 text-start d-flex align-items-center gap-3 px-4 py-2 border-0 bg-transparent ${
                item.danger ? 'text-danger' : ''
              }`}
              style={{borderRadius: 0}}
              onClick={item.onClick}
            >
              <i className={`bi ${item.icon} fs-5`} />
              <span>{item.label}</span>
            </button>
          )
        )}
    </div>
  )
}

export const PolicyChatPage: FC = () => {
  const {
    assignmentGroups,
    archiveThread,
    availableUsers,
    blockUser,
    clearThread,
    createDirectThread,
    createGroupThread,
    deleteThread,
    favoriteThread,
    fetchThreads,
    fetchUsers,
    labelThread,
    loadingMessages,
    loadingThreads,
    loadingUsers,
    markThreadUnread,
    messages,
    muteThread,
    notifyTyping,
    openAssignmentGroupThread,
    pinThread,
    selectedThread,
    sendMessage,
    sendingMessage,
    selectThread,
    toggleMessageReaction,
    threads,
    token,
    typingUsers,
  } = usePolicyChat()

  const [showDirectModal, setShowDirectModal] = useState(false)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [threadQuery, setThreadQuery] = useState('')
  const [userQuery, setUserQuery] = useState('')
  const [groupTitle, setGroupTitle] = useState('')
  const [groupUserIds, setGroupUserIds] = useState<number[]>([])
  const [showEmojiTray, setShowEmojiTray] = useState(false)
  const [draftMessage, setDraftMessage] = useState('')
  const [draftFiles, setDraftFiles] = useState<File[]>([])
  const [previewImage, setPreviewImage] = useState<{
    inlineUrl: string
    downloadUrl: string
    name: string
  } | null>(null)
  const [sidebarFilter, setSidebarFilter] = useState<SidebarFilter>('all')
  const [chatSearchOpen, setChatSearchOpen] = useState(false)
  const [chatSearchQuery, setChatSearchQuery] = useState('')
  const [chatSearchIndex, setChatSearchIndex] = useState(0)
  const [showInfoPanel, setShowInfoPanel] = useState(false)
  const [replyTo, setReplyTo] = useState<PolicyChatMessage | null>(null)
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null)
  const [contextMenu, setContextMenu] = useState<{
    thread: PolicyChatThread
    x: number
    y: number
  } | null>(null)
  const [messageMenu, setMessageMenu] = useState<{
    message: PolicyChatMessage
    x: number
    y: number
  } | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const initialScrollThreadIdRef = useRef<number | null>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingStreamRef = useRef<MediaStream | null>(null)
  const recordingChunksRef = useRef<Blob[]>([])
  const isTypingRef = useRef(false)

  useEffect(() => {
    const htmlElement = document.documentElement
    const bodyElement = document.body
    const appMain = document.getElementById('kt_app_main')
    const appContent = document.getElementById('kt_app_content')
    const appSlot = appContent?.parentElement
    const appContentContainer = document.getElementById('kt_app_content_container')

    htmlElement.classList.add('policychat-html-lock')
    bodyElement.classList.add('policychat-body-lock')
    appMain?.classList.add('policychat-app-main')
    appSlot?.classList.add('policychat-app-slot')
    appContent?.classList.add('policychat-app-content')
    appContentContainer?.classList.add('policychat-app-content-container')

    return () => {
      htmlElement.classList.remove('policychat-html-lock')
      bodyElement.classList.remove('policychat-body-lock')
      appMain?.classList.remove('policychat-app-main')
      appSlot?.classList.remove('policychat-app-slot')
      appContent?.classList.remove('policychat-app-content')
      appContentContainer?.classList.remove('policychat-app-content-container')
    }
  }, [])

  const handleDraftChange = (value: string) => {
    setDraftMessage(value)
    if (!selectedThread) return
    if (!isTypingRef.current) {
      isTypingRef.current = true
      void notifyTyping(selectedThread.id, true)
    }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false
      if (selectedThread) void notifyTyping(selectedThread.id, false)
    }, 3000)
  }

  const draftPreviews = useMemo<DraftPreview[]>(
    () =>
      draftFiles.map((file) => ({
        file,
        key: getDraftFileKey(file),
        url: canPreviewDraft(file) ? URL.createObjectURL(file) : null,
      })),
    [draftFiles]
  )

  const cleanupRecordingResources = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
      recordingTimerRef.current = null
    }
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop())
    recordingStreamRef.current = null
    mediaRecorderRef.current = null
    recordingChunksRef.current = []
    setIsRecording(false)
    setRecordingSeconds(0)
  }, [])

  const openContextMenuAt = useCallback((thread: PolicyChatThread, x: number, y: number) => {
    setContextMenu({thread, x, y})
  }, [])

  const finishRecording = useCallback(
    async (keepRecording: boolean) => {
      const recorder = mediaRecorderRef.current
      if (!recorder) return

      await new Promise<void>((resolve) => {
        const mimeType = recorder.mimeType || 'audio/webm'
        recorder.onstop = () => {
          const chunks = [...recordingChunksRef.current]
          cleanupRecordingResources()
          if (keepRecording && chunks.length > 0) {
            const blob = new Blob(chunks, {type: mimeType})
            const extension = mimeType.includes('ogg') ? 'ogg' : 'webm'
            const voiceNote = new File([blob], `voice-note-${Date.now()}.${extension}`, {
              type: mimeType,
            })
            setDraftFiles((current) => [...current, voiceNote])
          }
          resolve()
        }
        recorder.stop()
      })
    },
    [cleanupRecordingResources]
  )

  const startVoiceRecording = useCallback(async () => {
    if (!selectedThread) return
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice recording is not supported in this browser')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true})
      const mimeTypeCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
      const supportedMimeType = mimeTypeCandidates.find(
        (candidate) => typeof MediaRecorder.isTypeSupported !== 'function' || MediaRecorder.isTypeSupported(candidate)
      )

      const recorder = supportedMimeType
        ? new MediaRecorder(stream, {mimeType: supportedMimeType})
        : new MediaRecorder(stream)

      recordingStreamRef.current = stream
      mediaRecorderRef.current = recorder
      recordingChunksRef.current = []

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data)
        }
      }

      recorder.start()
      setIsRecording(true)
      setRecordingSeconds(0)
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((current) => current + 1)
      }, 1000)
    } catch (error) {
      toast.error('Unable to access the microphone')
    }
  }, [selectedThread])

  useEffect(() => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
    isTypingRef.current = false
  }, [selectedThread?.id])

  useEffect(
    () => () => {
      draftPreviews.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url)
      })
    },
    [draftPreviews]
  )

  useEffect(
    () => () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
      cleanupRecordingResources()
    },
    [cleanupRecordingResources]
  )

  useEffect(() => {
    const composer = composerRef.current
    if (!composer) return
    composer.style.height = '0px'
    composer.style.height = `${Math.min(composer.scrollHeight, 140)}px`
  }, [draftMessage])

  useEffect(() => {
    if (!selectedThread?.id || isRecording) return
    requestAnimationFrame(() => composerRef.current?.focus())
  }, [isRecording, replyTo, selectedThread?.id])

  useEffect(() => {
    if (!chatSearchOpen) return
    requestAnimationFrame(() => searchInputRef.current?.focus())
  }, [chatSearchOpen])

  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null

  const isNearBottom = (element: HTMLElement, threshold = 120) =>
    element.scrollHeight - element.scrollTop - element.clientHeight < threshold

  const scrollMessagesToBottom = (smooth = true) => {
    const element = messagesContainerRef.current
    if (!element) return
    element.scrollTo({top: element.scrollHeight, behavior: smooth ? 'smooth' : 'auto'})
  }

  useLayoutEffect(() => {
    if (!selectedThread?.id) return
    if (messages.length === 0) return
    if (messages[0].thread_id !== selectedThread.id) return
    if (initialScrollThreadIdRef.current === selectedThread.id) return

    initialScrollThreadIdRef.current = selectedThread.id

    const scrollToBottom = () => {
      const el = messagesContainerRef.current
      if (!el) return
      el.scrollTop = el.scrollHeight
      setShowScrollToBottom(false)
    }

    scrollToBottom()
    requestAnimationFrame(scrollToBottom)
    const timer1 = window.setTimeout(scrollToBottom, 80)
    const timer2 = window.setTimeout(scrollToBottom, 250)
    return () => {
      window.clearTimeout(timer1)
      window.clearTimeout(timer2)
    }
  }, [messages, selectedThread?.id])

  useEffect(() => {
    if (lastMessageId === null) return
    if (initialScrollThreadIdRef.current !== selectedThread?.id) return
    const element = messagesContainerRef.current
    if (!element) return
    if (isNearBottom(element, 240)) {
      element.scrollTo({top: element.scrollHeight, behavior: 'smooth'})
    } else {
      setShowScrollToBottom(true)
    }
  }, [lastMessageId, selectedThread?.id])

  useEffect(() => {
    if (selectedThread?.id) {
      initialScrollThreadIdRef.current = null
      setShowScrollToBottom(false)
      setShowInfoPanel(false)
      setChatSearchOpen(false)
      setChatSearchQuery('')
      setChatSearchIndex(0)
      setReplyTo(null)
      setShowEmojiTray(false)
      setMessageMenu(null)
      setHighlightedMessageId(null)
    }
  }, [selectedThread?.id])

  useEffect(() => {
    if (mediaRecorderRef.current) {
      void finishRecording(false)
    }
  }, [finishRecording, selectedThread?.id])

  useEffect(() => {
    setChatSearchIndex(0)
  }, [chatSearchQuery, selectedThread?.id])

  const handleMessagesScroll = () => {
    const element = messagesContainerRef.current
    if (!element) return
    setShowScrollToBottom(!isNearBottom(element))
  }

  const linkedThreadByAssignmentGroupId = useMemo(() => {
    const next = new Map<number, PolicyChatThread>()
    threads.forEach((thread) => {
      if (thread.assignment_group?.id) {
        next.set(thread.assignment_group.id, thread)
      }
    })
    return next
  }, [threads])

  const selectedThreadAvatar = useMemo(
    () => (selectedThread ? getThreadAvatarTarget(selectedThread) : null),
    [selectedThread]
  )

  const otherParticipant = useMemo(
    () => selectedThread?.participants.find((participant) => !participant.is_self)?.user || null,
    [selectedThread]
  )

  const normalizedChatSearchQuery = chatSearchQuery.trim().toLowerCase()

  const messageSearchResults = useMemo(() => {
    if (!selectedThread?.id || !normalizedChatSearchQuery) return []

    return messages.filter((message) => {
      const attachmentText = message.attachments
        .map((attachment) => `${attachment.original_name} ${getAttachmentLabel(attachment.attachment_type)}`)
        .join(' ')
      const haystack = [
        message.text,
        message.sender?.full_name || '',
        attachmentText,
        message.reply_to?.text || '',
      ]
        .join(' ')
        .toLowerCase()

      return haystack.includes(normalizedChatSearchQuery)
    })
  }, [messages, normalizedChatSearchQuery, selectedThread?.id])

  const messageSearchIds = useMemo(
    () => new Set(messageSearchResults.map((message) => message.id)),
    [messageSearchResults]
  )

  const activeSearchMessageId = useMemo(() => {
    if (messageSearchResults.length === 0) return null
    const nextIndex =
      ((chatSearchIndex % messageSearchResults.length) + messageSearchResults.length) %
      messageSearchResults.length
    return messageSearchResults[nextIndex].id
  }, [chatSearchIndex, messageSearchResults])

  const attachmentSummary = useMemo(() => {
    const summary = {
      IMAGE: 0,
      VIDEO: 0,
      AUDIO: 0,
      FILE: 0,
    }

    messages.forEach((message) => {
      message.attachments.forEach((attachment) => {
        summary[attachment.attachment_type] += 1
      })
    })

    return summary
  }, [messages])

  const sharedMediaItems = useMemo(
    () =>
      messages
        .flatMap((message) =>
          message.attachments
            .filter(
              (attachment) =>
                attachment.attachment_type === 'IMAGE' || attachment.attachment_type === 'VIDEO'
            )
            .map((attachment) => ({
              attachment,
              createdAt: message.created_at,
              messageId: message.id,
            }))
        )
        .slice(-6)
        .reverse(),
    [messages]
  )

  const recentAttachmentItems = useMemo(
    () =>
      messages
        .flatMap((message) =>
          message.attachments
            .filter(
              (attachment) =>
                attachment.attachment_type === 'AUDIO' || attachment.attachment_type === 'FILE'
            )
            .map((attachment) => ({
              attachment,
              createdAt: message.created_at,
              messageId: message.id,
              senderName: message.sender?.full_name || 'User',
            }))
        )
        .slice(-6)
        .reverse(),
    [messages]
  )

  const filterCounts = useMemo<Record<SidebarFilter, number>>(
    () => ({
      all: threads.filter((thread) => !thread.is_archived).length,
      unread: threads.filter((thread) => !thread.is_archived && thread.unread_count > 0).length,
      favorites: threads.filter((thread) => !thread.is_archived && thread.is_favorite).length,
      groups:
        threads.filter((thread) => !thread.is_archived && thread.thread_type === 'GROUP').length +
        assignmentGroups.filter((group) => !linkedThreadByAssignmentGroupId.has(group.id)).length,
      archived: threads.filter((thread) => thread.is_archived).length,
    }),
    [assignmentGroups, linkedThreadByAssignmentGroupId, threads]
  )

  const filteredThreads = useMemo(() => {
    const visible = threads.filter((thread) => {
      if (sidebarFilter === 'unread') return !thread.is_archived && thread.unread_count > 0
      if (sidebarFilter === 'favorites') return !thread.is_archived && thread.is_favorite
      if (sidebarFilter === 'groups') return !thread.is_archived && thread.thread_type === 'GROUP'
      if (sidebarFilter === 'archived') return thread.is_archived
      return !thread.is_archived
    })
    const normalizedQuery = threadQuery.trim().toLowerCase()
    if (!normalizedQuery) return visible
    return visible.filter((thread) => {
      const haystack = `${thread.display_name} ${getThreadSnippet(thread)}`.toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [sidebarFilter, threadQuery, threads])

  const filteredAssignmentGroups = useMemo(() => {
    if (sidebarFilter !== 'groups') return []

    const normalizedQuery = threadQuery.trim().toLowerCase()
    return assignmentGroups.filter((group) => {
      const linkedThread = linkedThreadByAssignmentGroupId.get(group.id)
      if (linkedThread) return false

      if (!normalizedQuery) return true
      const haystack = `${group.name} ${group.participant_preview.join(' ')}`.toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [assignmentGroups, linkedThreadByAssignmentGroupId, sidebarFilter, threadQuery])

  const sidebarEntries = useMemo<SidebarGroupEntry[]>(() => {
    const threadEntries = filteredThreads.map((thread) => ({kind: 'thread', thread}) as SidebarGroupEntry)
    if (sidebarFilter !== 'groups') return threadEntries

    const assignmentGroupEntries = filteredAssignmentGroups
      .slice()
      .sort((left, right) => left.name.localeCompare(right.name))
      .map((group) => ({kind: 'assignment-group', group}) as SidebarGroupEntry)

    return [...threadEntries, ...assignmentGroupEntries]
  }, [filteredAssignmentGroups, filteredThreads, sidebarFilter])

  const handleContextMenu = (event: ReactMouseEvent, thread: PolicyChatThread) => {
    event.preventDefault()
    const menuWidth = 240
    const menuHeight = 380
    const x = Math.min(event.clientX, window.innerWidth - menuWidth - 8)
    const y = Math.min(event.clientY, window.innerHeight - menuHeight - 8)
    openContextMenuAt(thread, x, y)
  }

  const handleHeaderMenu = (event: ReactMouseEvent<HTMLButtonElement>, thread: PolicyChatThread) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = Math.min(rect.right - 220, window.innerWidth - 248)
    const y = Math.min(rect.bottom + 8, window.innerHeight - 388)
    openContextMenuAt(thread, x, y)
  }

  const closeContextMenu = useCallback(() => setContextMenu(null), [])
  const closeMessageMenu = useCallback(() => setMessageMenu(null), [])
  const closeChatSearch = useCallback(() => {
    setChatSearchOpen(false)
    setChatSearchQuery('')
    setChatSearchIndex(0)
  }, [])

  const goToMessage = useCallback((messageId: number, behavior: ScrollBehavior = 'smooth') => {
    const target = messageRefs.current[messageId]
    if (!target) {
      toast.info('That message is not loaded in the current view')
      return
    }

    target.scrollIntoView({behavior, block: 'center'})
    setHighlightedMessageId(messageId)

    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedMessageId((current) => (current === messageId ? null : current))
      highlightTimerRef.current = null
    }, 1800)
  }, [])

  useEffect(() => {
    if (!chatSearchOpen || !activeSearchMessageId) return
    requestAnimationFrame(() => goToMessage(activeSearchMessageId))
  }, [activeSearchMessageId, chatSearchOpen, goToMessage])

  useEffect(() => {
    if (messageSearchResults.length === 0) {
      if (chatSearchIndex !== 0) setChatSearchIndex(0)
      return
    }

    if (chatSearchIndex >= messageSearchResults.length) {
      setChatSearchIndex(0)
    }
  }, [chatSearchIndex, messageSearchResults.length])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f' && selectedThread?.id) {
        event.preventDefault()
        setChatSearchOpen(true)
        return
      }

      if (event.key === 'Escape') {
        closeMessageMenu()
        if (showInfoPanel) setShowInfoPanel(false)
        if (chatSearchOpen) closeChatSearch()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [chatSearchOpen, closeChatSearch, closeMessageMenu, selectedThread?.id, showInfoPanel])

  const runWithMenuClose = (action: () => Promise<unknown> | void) => {
    closeContextMenu()
    void action()
  }

  const openMessageMenu = (
    event: ReactMouseEvent<HTMLButtonElement | HTMLDivElement>,
    message: PolicyChatMessage
  ) => {
    event.preventDefault()
    event.stopPropagation()
    const menuWidth = 190
    const menuHeight = 160
    const x = Math.min(event.clientX, window.innerWidth - menuWidth - 8)
    const y = Math.min(event.clientY, window.innerHeight - menuHeight - 8)
    setMessageMenu({message, x, y})
  }

  const handleCopyMessage = async (message: PolicyChatMessage) => {
    if (!message.text.trim()) return
    try {
      await navigator.clipboard.writeText(message.text)
      toast.success('Message copied')
    } catch (error) {
      toast.error('Unable to copy the message')
    }
  }

  const handleOpenAttachment = (message: PolicyChatMessage) => {
    const attachment = message.attachments[0]
    if (!attachment) return
    window.open(buildAttachmentUrl(attachment, token, false), '_blank', 'noopener,noreferrer')
  }

  const handleToggleReaction = async (message: PolicyChatMessage, emoji: string) => {
    await toggleMessageReaction(message.id, emoji)
  }

  const handleJumpToReply = (message: PolicyChatMessage) => {
    if (!message.reply_to?.id) return
    goToMessage(message.reply_to.id)
  }

  const handleCycleSearchResult = (direction: 1 | -1) => {
    if (messageSearchResults.length === 0) return

    setChatSearchIndex((current) => {
      const next = current + direction
      if (next < 0) return messageSearchResults.length - 1
      return next % messageSearchResults.length
    })
  }

  const handleLabel = (thread: PolicyChatThread) => {
    const next = window.prompt('Label for this chat (leave blank to remove)', thread.label || '')
    if (next === null) return
    void labelThread(thread.id, next.trim())
  }

  const handleBlock = (thread: PolicyChatThread) => {
    const other = thread.participants.find((participant) => !participant.is_self)?.user
    if (!other) return
    if (!window.confirm(`Block ${other.full_name}? They will not be able to reach you on PolicyChat.`)) {
      return
    }
    void blockUser(other.id, true)
  }

  const handleClear = (thread: PolicyChatThread) => {
    if (!window.confirm('Clear all messages in this chat? This only hides them for you.')) return
    void clearThread(thread.id)
  }

  const handleDelete = (thread: PolicyChatThread) => {
    if (!window.confirm('Delete this chat? It will be removed from your list.')) return
    void deleteThread(thread.id)
  }

  const filteredUsers = useMemo(() => {
    const normalizedQuery = userQuery.trim().toLowerCase()
    if (!normalizedQuery) return availableUsers
    return availableUsers.filter((candidate) => {
      const haystack = `${candidate.full_name} ${candidate.email}`.toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [availableUsers, userQuery])

  const handleOpenDirectModal = async () => {
    setUserQuery('')
    setShowDirectModal(true)
    await fetchUsers('')
  }

  const handleOpenGroupModal = async () => {
    setUserQuery('')
    setGroupTitle('')
    setGroupUserIds([])
    setShowGroupModal(true)
    await fetchUsers('')
  }

  const handleUserQueryChange = async (value: string) => {
    setUserQuery(value)
    await fetchUsers(value)
  }

  const handleCreateDirect = async (userId: number) => {
    const thread = await createDirectThread(userId)
    if (thread) {
      setShowDirectModal(false)
    }
  }

  const handleToggleGroupUser = (userId: number) => {
    setGroupUserIds((current) =>
      current.includes(userId)
        ? current.filter((candidate) => candidate !== userId)
        : [...current, userId]
    )
  }

  const handleCreateGroup = async () => {
    const thread = await createGroupThread(groupTitle.trim(), groupUserIds)
    if (thread) {
      setShowGroupModal(false)
    }
  }

  const handleOpenAssignmentGroup = async (group: PolicyChatAssignmentGroup) => {
    await openAssignmentGroupThread(group.id)
  }

  const handleDraftFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setDraftFiles((current) => [...current, ...files])
    event.target.value = ''
  }

  const handleComposerPaste = (event: ReactClipboardEvent<HTMLTextAreaElement>) => {
    if (isRecording) return
    const items = event.clipboardData?.items
    if (!items) return
    const files: File[] = []
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile()
        if (file) files.push(file)
      }
    }
    if (files.length === 0) return
    event.preventDefault()
    setDraftFiles((current) => [...current, ...files])
  }

  const handleSend = async () => {
    if (!selectedThread || isRecording) return
    const sent = await sendMessage(selectedThread.id, draftMessage, draftFiles, replyTo?.id)
    if (sent) {
      setDraftMessage('')
      setDraftFiles([])
      setReplyTo(null)
      if (isTypingRef.current) {
        isTypingRef.current = false
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
        void notifyTyping(selectedThread.id, false)
      }
    }
  }

  const addEmoji = (emoji: string) => {
    handleDraftChange(`${draftMessage}${emoji}`)
  }

  const removeDraftFile = (fileKey: string) => {
    setDraftFiles((current) => current.filter((file) => getDraftFileKey(file) !== fileKey))
  }

  const handleComposerKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void handleSend()
    }
  }

  const participantsSummary = selectedThread?.participants
    .filter((participant) => !participant.is_self)
    .map((participant) => participant.user.full_name)
    .join(', ')

  const typingIndicator = useMemo(() => {
    if (!selectedThread) return ''
    const map = typingUsers[selectedThread.id]
    if (!map) return ''
    const now = Date.now()
    const names = Object.values(map)
      .filter((entry) => entry.expiresAt > now)
      .map((entry) => entry.fullName)
    if (names.length === 0) return ''
    if (names.length === 1) return `${names[0]} is typing...`
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`
    return `${names[0]} and ${names.length - 1} others are typing...`
  }, [selectedThread, typingUsers])

  const hasComposerPayload = draftMessage.trim().length > 0 || draftFiles.length > 0

  return (
    <>
      <PageTitle breadcrumbs={[]}>PolicyChat</PageTitle>

      <div className='policychat-page'>
        <div className='card h-100 policychat-shell'>
          <div className='card-body p-0 policychat-shell-body'>
            <div className='policychat-layout'>
            <div className='policychat-sidebar'>
              <div className='border-bottom policychat-sidebar-header'>
                <div className='policychat-sidebar-topbar'>
                  <h2 className='policychat-sidebar-title mb-0'>PolicyChat</h2>
                  <div className='policychat-sidebar-actions'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light policychat-icon-button'
                      onClick={() => void fetchThreads()}
                      title='Refresh chats'
                    >
                      <i className='bi bi-arrow-clockwise fs-5' />
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-light policychat-icon-button'
                      onClick={() => void handleOpenDirectModal()}
                      title='New direct chat'
                    >
                      <i className='bi bi-chat-left-text fs-5' />
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-light policychat-icon-button'
                      onClick={() => void handleOpenGroupModal()}
                      title='New group chat'
                    >
                      <i className='bi bi-people fs-5' />
                    </button>
                  </div>
                </div>
                <input
                  type='text'
                  className='form-control policychat-search-input policychat-search-input--sidebar'
                  placeholder='Search or start a new chat'
                  value={threadQuery}
                  onChange={(event) => setThreadQuery(event.target.value)}
                />
                <div className='policychat-filter-chips policychat-filter-chips--sidebar'>
                  {FILTER_ORDER.map((filterId) => (
                    <button
                      key={filterId}
                      type='button'
                      className={`policychat-filter-chip ${
                        sidebarFilter === filterId ? 'policychat-filter-chip--active' : ''
                      }`}
                      onClick={() => setSidebarFilter(filterId)}
                    >
                      <span className='text-capitalize'>{filterId}</span>
                      {filterCounts[filterId] > 0 && (
                        <span className='policychat-filter-count'>{filterCounts[filterId]}</span>
                      )}
                    </button>
                  ))}
                </div>
                {sidebarFilter === 'groups' && (
                  <div className='policychat-sidebar-helper'>
                    Assignment groups show if you belong to them, or if you are admin/super user.
                  </div>
                )}
              </div>

              <div className='policychat-thread-list'>
                {loadingThreads && <div className='p-6 text-muted'>Loading conversations...</div>}
                {!loadingThreads && sidebarEntries.length === 0 && (
                  <div className='p-6 text-muted'>No internal conversations yet.</div>
                )}
                {!loadingThreads &&
                  sidebarEntries.map((entry, index) => {
                    const showAssignmentGroupHeader =
                      sidebarFilter === 'groups' &&
                      entry.kind === 'assignment-group' &&
                      (index === 0 || sidebarEntries[index - 1]?.kind !== 'assignment-group')

                    if (entry.kind === 'assignment-group') {
                      const group = entry.group
                      return (
                        <Fragment key={`assignment-group-block-${group.id}`}>
                          {showAssignmentGroupHeader && (
                            <div
                              className='px-6 py-2 text-uppercase text-muted fw-semibold fs-9 policychat-thread-section'
                            >
                              Assignment groups
                            </div>
                          )}
                          <button
                            key={`assignment-group-${group.id}`}
                            type='button'
                            className='btn btn-active-light d-flex w-100 text-start rounded-0 border-bottom align-items-center policychat-thread policychat-thread-row'
                            onClick={() => void handleOpenAssignmentGroup(group)}
                          >
                            <UserAvatar
                              name={group.name}
                              colorKey={`assignment-group-${group.id}`}
                              groupIcon
                              size={44}
                            />
                            <div className='flex-grow-1 overflow-hidden'>
                              <div className='d-flex justify-content-between align-items-start gap-2'>
                                <div className='fw-bold text-gray-900 text-truncate d-flex align-items-center gap-2'>
                                  <span className='text-truncate'>{group.name}</span>
                                </div>
                                <div className='text-muted fs-8 flex-shrink-0'>
                                  {group.participant_count} members
                                </div>
                              </div>
                              <div className='d-flex align-items-center gap-2 mt-1'>
                                <span className='badge badge-light-primary fs-8'>
                                  Assignment group
                                </span>
                                <div className='text-muted fs-7 text-truncate flex-grow-1'>
                                  {getAssignmentGroupSnippet(group)}
                                </div>
                              </div>
                            </div>
                            <span className='text-primary fs-7 fw-semibold ms-3'>Open</span>
                          </button>
                        </Fragment>
                      )
                    }

                    const thread = entry.thread
                    const avatarTarget = getThreadAvatarTarget(thread)
                    return (
                      <button
                        key={thread.id}
                        type='button'
                        className={`btn btn-active-light d-flex w-100 text-start rounded-0 border-bottom align-items-center policychat-thread policychat-thread-row ${
                          selectedThread?.id === thread.id ? 'policychat-thread--active' : ''
                        }`}
                        onClick={() => void selectThread(thread)}
                        onContextMenu={(event) => handleContextMenu(event, thread)}
                      >
                        <UserAvatar
                          name={avatarTarget.name}
                          avatar={avatarTarget.avatar}
                          colorKey={avatarTarget.colorKey}
                          groupIcon={avatarTarget.groupIcon}
                          size={44}
                        />
                        <div className='flex-grow-1 overflow-hidden'>
                          <div className='d-flex justify-content-between align-items-start gap-2'>
                            <div className='fw-bold text-gray-900 text-truncate d-flex align-items-center gap-2'>
                              {thread.is_pinned && <i className='bi bi-pin-angle-fill text-muted fs-7' />}
                              {thread.is_favorite && <i className='bi bi-heart-fill text-danger fs-7' />}
                              <span className='text-truncate'>{thread.display_name}</span>
                            </div>
                            <div className='text-muted fs-8 flex-shrink-0 d-flex align-items-center gap-2'>
                              {(thread.muted || Boolean(thread.mute_until)) && (
                                <i className='bi bi-bell-slash fs-7' />
                              )}
                              {formatDateTime(thread.last_activity_at)}
                            </div>
                          </div>
                          <div className='d-flex align-items-center gap-2 mt-1'>
                            {thread.assignment_group && (
                              <span className='badge badge-light-primary fs-8'>Assignment group</span>
                            )}
                            {thread.label && (
                              <span className='badge badge-light-info fs-8'>{thread.label}</span>
                            )}
                            {(() => {
                              const threadTyping = typingUsers[thread.id] || {}
                              const typingActive = Object.values(threadTyping).some(
                                (entryTyping) => entryTyping.expiresAt > Date.now()
                              )
                              if (typingActive) {
                                return (
                                  <div className='text-primary fs-7 fst-italic text-truncate flex-grow-1'>
                                    typing...
                                  </div>
                                )
                              }
                              return (
                                <>
                                  {isLastMessageOwn(thread) && (
                                    <ReadTicks read={isLastMessageRead(thread)} ownBubble={false} />
                                  )}
                                  <div className='text-muted fs-7 text-truncate flex-grow-1'>
                                    {getThreadSnippet(thread)}
                                  </div>
                                </>
                              )
                            })()}
                          </div>
                        </div>
                        {thread.unread_count > 0 && (
                          <span className='badge badge-circle badge-primary ms-3'>{thread.unread_count}</span>
                        )}
                      </button>
                    )
                  })}
              </div>
            </div>

            <div className='policychat-conversation'>
              {selectedThread ? (
                <>
                  <div className='border-bottom policychat-conversation-header'>
                    <div className='d-flex align-items-center justify-content-between gap-4'>
                      <div className='d-flex align-items-center gap-3'>
                        {selectedThreadAvatar && (
                          <UserAvatar
                            name={selectedThreadAvatar.name}
                            avatar={selectedThreadAvatar.avatar}
                            colorKey={selectedThreadAvatar.colorKey}
                            groupIcon={selectedThreadAvatar.groupIcon}
                            size={44}
                          />
                        )}
                        <div className='policychat-conversation-identity'>
                          <h3 className='policychat-conversation-name mb-0'>{selectedThread.display_name}</h3>
                          <div className='policychat-conversation-subtitle'>
                            {typingIndicator ? (
                              <span className='text-primary fst-italic'>{typingIndicator}</span>
                            ) : selectedThread.thread_type === 'GROUP' ? (
                              participantsSummary || 'Internal group'
                            ) : (
                              otherParticipant?.email
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='d-flex align-items-center gap-2 policychat-conversation-actions'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light policychat-header-pill'
                          onClick={() => handleLabel(selectedThread)}
                        >
                          <i className='bi bi-tag me-2' />
                          {selectedThread.label || 'Label chat'}
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light policychat-icon-button'
                          title='Chat info'
                          onClick={() => setShowInfoPanel((current) => !current)}
                        >
                          <i
                            className={`bi ${selectedThread.thread_type === 'GROUP' ? 'bi-people' : 'bi-person'} fs-5`}
                          />
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light policychat-icon-button'
                          title='Search in conversation'
                          onClick={() => {
                            if (chatSearchOpen) {
                              closeChatSearch()
                              return
                            }
                            setChatSearchOpen(true)
                          }}
                        >
                          <i className='bi bi-search fs-5' />
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light policychat-icon-button'
                          title='More'
                          onClick={(event) => handleHeaderMenu(event, selectedThread)}
                        >
                          <i className='bi bi-three-dots-vertical fs-5' />
                        </button>
                      </div>
                    </div>
                    {chatSearchOpen && (
                      <div className='policychat-chat-search'>
                        <div className='policychat-chat-search-input-wrap'>
                          <i className='bi bi-search policychat-chat-search-input-icon' />
                          <input
                            ref={searchInputRef}
                            type='text'
                            className='policychat-chat-search-input'
                            placeholder='Search in conversation'
                            value={chatSearchQuery}
                            onChange={(event) => setChatSearchQuery(event.target.value)}
                          />
                        </div>
                        <div className='policychat-chat-search-meta'>
                          <span className='policychat-chat-search-count'>
                            {!chatSearchQuery.trim()
                              ? 'Type'
                              : messageSearchResults.length > 0
                              ? `${chatSearchIndex + 1}/${messageSearchResults.length}`
                              : '0 results'}
                          </span>
                          <button
                            type='button'
                            className='btn btn-sm btn-light btn-icon policychat-icon-button'
                            disabled={messageSearchResults.length === 0}
                            onClick={() => handleCycleSearchResult(-1)}
                            title='Previous result'
                          >
                            <i className='bi bi-chevron-up fs-7' />
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-light btn-icon policychat-icon-button'
                            disabled={messageSearchResults.length === 0}
                            onClick={() => handleCycleSearchResult(1)}
                            title='Next result'
                          >
                            <i className='bi bi-chevron-down fs-7' />
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-light btn-icon policychat-icon-button'
                            onClick={closeChatSearch}
                            title='Close search'
                          >
                            <i className='bi bi-x-lg fs-7' />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    ref={messagesContainerRef}
                    onScroll={handleMessagesScroll}
                    className='flex-grow-1 position-relative policychat-messages'
                  >
                    {loadingMessages && <div className='text-muted'>Loading messages...</div>}
                    {!loadingMessages && messages.length === 0 && (
                      <div className='text-center text-muted py-10'>
                        This chat is ready. Type the first message.
                      </div>
                    )}
                    {!loadingMessages &&
                      messages.map((message: PolicyChatMessage, index: number) => {
                        const previous = index > 0 ? messages[index - 1] : null
                        const next = index < messages.length - 1 ? messages[index + 1] : null
                        const sameSenderAsPrevious =
                          !!previous &&
                          previous.is_own === message.is_own &&
                          previous.sender?.id === message.sender?.id &&
                          startOfDay(new Date(previous.created_at)) ===
                            startOfDay(new Date(message.created_at))
                        const sameSenderAsNext =
                          !!next &&
                          next.is_own === message.is_own &&
                          next.sender?.id === message.sender?.id &&
                          startOfDay(new Date(next.created_at)) ===
                            startOfDay(new Date(message.created_at))
                        const showSenderName =
                          selectedThread.thread_type === 'GROUP' &&
                          !message.is_own &&
                          !sameSenderAsPrevious
                        const showAvatar = !message.is_own && !sameSenderAsNext
                        const isSearchMatch = messageSearchIds.has(message.id)
                        const isActiveSearchMatch = activeSearchMessageId === message.id
                        const isJumpHighlighted = highlightedMessageId === message.id
                        const showDayDivider =
                          !previous ||
                          startOfDay(new Date(previous.created_at)) !==
                            startOfDay(new Date(message.created_at))
                        return (
                          <div
                            key={message.id}
                            ref={(node) => {
                              messageRefs.current[message.id] = node
                            }}
                          >
                            {showDayDivider && (
                              <div className='d-flex justify-content-center my-4 policychat-day-divider-wrap'>
                                <span
                                  className='badge fs-8 px-3 py-2 policychat-day-divider'
                                >
                                  {formatDayLabel(message.created_at)}
                                </span>
                              </div>
                            )}
                            <div
                              className={`d-flex align-items-end gap-2 ${
                                sameSenderAsPrevious ? 'mb-2' : 'mb-5'
                              } ${
                                message.is_own ? 'justify-content-end' : 'justify-content-start'
                              }`}
                            >
                              {showAvatar ? (
                                <UserAvatar
                                  name={message.sender?.full_name || 'User'}
                                  avatar={message.sender?.avatar}
                                  colorKey={message.sender?.id ?? 0}
                                  size={32}
                                />
                              ) : !message.is_own ? (
                                <div style={{width: '32px', flexShrink: 0}} />
                              ) : null}
                              <div
                                className={`policychat-bubble-wrap ${message.is_own ? 'policychat-bubble-wrap--own' : ''}`}
                                onContextMenu={(event) => openMessageMenu(event, message)}
                              >
                                <button
                                  type='button'
                                  className='btn btn-sm btn-icon policychat-message-menu-trigger'
                                  title='Message actions'
                                  onClick={(event) => openMessageMenu(event, message)}
                                >
                                  <i className='bi bi-chevron-down fs-7' />
                                </button>
                                <div
                                  className={`rounded px-4 py-3 shadow-sm position-relative policychat-bubble ${
                                    message.is_own
                                      ? 'policychat-bubble--own text-gray-900'
                                      : 'policychat-bubble--other'
                                  } ${
                                    isActiveSearchMatch
                                      ? 'policychat-bubble--search-active'
                                      : isSearchMatch
                                      ? 'policychat-bubble--search-match'
                                      : ''
                                  } ${
                                    isJumpHighlighted ? 'policychat-bubble--jump-highlight' : ''
                                  } ${
                                    !sameSenderAsNext
                                      ? message.is_own
                                        ? 'policychat-bubble--tail-own'
                                        : 'policychat-bubble--tail-other'
                                      : ''
                                  }`}
                                  style={{maxWidth: '75%'}}
                                >
                                  {message.reply_to && (
                                    <button
                                      type='button'
                                      className='policychat-reply-preview rounded px-3 py-2 mb-2'
                                      onClick={() => handleJumpToReply(message)}
                                      style={{
                                        backgroundColor: message.is_own
                                          ? 'rgba(255, 255, 255, 0.2)'
                                          : 'rgba(13, 110, 253, 0.08)',
                                        borderLeft: '3px solid',
                                        borderLeftColor: message.is_own
                                          ? 'rgba(255, 255, 255, 0.7)'
                                          : '#0d6efd',
                                      }}
                                    >
                                      <div
                                        className={`fw-bold fs-8 ${
                                          message.is_own ? 'text-white opacity-75' : 'text-primary'
                                        }`}
                                      >
                                        {message.reply_to.sender?.full_name || 'User'}
                                      </div>
                                      <div
                                        className={`fs-8 text-truncate ${
                                          message.is_own ? 'text-gray-700' : 'text-muted'
                                        }`}
                                        style={{maxWidth: '320px'}}
                                      >
                                        {message.reply_to.text ||
                                          (message.reply_to.attachments?.length
                                            ? getAttachmentLabel(message.reply_to.attachments[0].attachment_type)
                                            : message.reply_to.message_type)}
                                      </div>
                                    </button>
                                  )}
                                  {showSenderName && (
                                    <div className='fw-bold fs-8 mb-1 text-primary'>
                                      {message.sender?.full_name || 'User'}
                                    </div>
                                  )}
                                  {message.text && (
                                    <div style={{whiteSpace: 'pre-wrap'}}>
                                      {renderHighlightedText(message.text, isSearchMatch ? chatSearchQuery : '')}
                                    </div>
                                  )}
                                  {message.attachments.map((attachment) =>
                                    renderAttachment(
                                      attachment,
                                      token,
                                      message.is_own,
                                      (att, inlineUrl, downloadUrl) =>
                                        setPreviewImage({
                                          inlineUrl,
                                          downloadUrl,
                                          name: att.original_name,
                                        })
                                    )
                                  )}
                                  <div
                                    className={`mt-2 fs-8 d-flex align-items-center justify-content-end gap-1 ${
                                      message.is_own ? 'text-gray-700' : 'text-muted'
                                    }`}
                                  >
                                    <span>{formatDateTime(message.created_at)}</span>
                                    {message.is_own && (
                                      <ReadTicks
                                        read={isMessageReadByOthers(message, selectedThread)}
                                        ownBubble
                                      />
                                    )}
                                  </div>
                                </div>
                                {message.reactions.length > 0 && (
                                  <div
                                    className={`policychat-reaction-row ${
                                      message.is_own ? 'policychat-reaction-row--own' : ''
                                    }`}
                                  >
                                    {message.reactions.map((reaction) => (
                                      <button
                                        key={`${message.id}-${reaction.emoji}`}
                                        type='button'
                                        className={`policychat-reaction-chip ${
                                          reaction.reacted_by_self
                                            ? 'policychat-reaction-chip--active'
                                            : ''
                                        }`}
                                        onClick={() => void handleToggleReaction(message, reaction.emoji)}
                                        title={getReactionTooltip(reaction)}
                                      >
                                        <span>{reaction.emoji}</span>
                                        <span className='policychat-reaction-chip-count'>
                                          {reaction.count}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}

                    {showScrollToBottom && (
                      <button
                        type='button'
                        className='btn btn-light btn-icon shadow position-sticky policychat-scroll-button'
                        style={{
                          bottom: '16px',
                          left: 'calc(100% - 56px)',
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          float: 'right',
                          zIndex: 5,
                        }}
                        onClick={() => {
                          scrollMessagesToBottom(true)
                          setShowScrollToBottom(false)
                        }}
                      >
                        <i className='bi bi-arrow-down fs-3' />
                      </button>
                    )}
                  </div>

                  {showInfoPanel && (
                    <>
                      <button
                        type='button'
                        className='policychat-info-panel-backdrop'
                        onClick={() => setShowInfoPanel(false)}
                        aria-label='Close chat info'
                      />
                      <aside className='policychat-info-panel'>
                        <div className='policychat-info-panel-header'>
                          <h4 className='mb-0'>Chat info</h4>
                          <button
                            type='button'
                            className='btn btn-sm btn-light btn-icon policychat-icon-button'
                            onClick={() => setShowInfoPanel(false)}
                          >
                            <i className='bi bi-x-lg fs-7' />
                          </button>
                        </div>
                        <div className='policychat-info-panel-body'>
                          <div className='policychat-info-hero'>
                            {selectedThreadAvatar && (
                              <UserAvatar
                                name={selectedThreadAvatar.name}
                                avatar={selectedThreadAvatar.avatar}
                                colorKey={selectedThreadAvatar.colorKey}
                                groupIcon={selectedThreadAvatar.groupIcon}
                                size={82}
                              />
                            )}
                            <div>
                              <h3 className='mb-1'>{selectedThread.display_name}</h3>
                              <div className='text-muted fs-7'>
                                {selectedThread.thread_type === 'GROUP'
                                  ? `${selectedThread.participant_count} participants`
                                  : otherParticipant?.email || 'Direct conversation'}
                              </div>
                            </div>
                          </div>

                          <div className='policychat-info-badges'>
                            {selectedThread.label && (
                              <span className='badge badge-light-info'>{selectedThread.label}</span>
                            )}
                            {selectedThread.is_favorite && (
                              <span className='badge badge-light-danger'>Favorite</span>
                            )}
                            {selectedThread.is_pinned && <span className='badge badge-light'>Pinned</span>}
                            {(selectedThread.muted || Boolean(selectedThread.mute_until)) && (
                              <span className='badge badge-light-warning'>Muted</span>
                            )}
                            {selectedThread.is_archived && (
                              <span className='badge badge-light-dark'>Archived</span>
                            )}
                          </div>

                          <div className='policychat-info-section'>
                            <div className='policychat-info-section-title'>Media, voice and files</div>
                            <div className='policychat-info-stats'>
                              <div className='policychat-info-stat'>
                                <div className='policychat-info-stat-value'>
                                  {attachmentSummary.IMAGE + attachmentSummary.VIDEO}
                                </div>
                                <div className='policychat-info-stat-label'>Media</div>
                              </div>
                              <div className='policychat-info-stat'>
                                <div className='policychat-info-stat-value'>{attachmentSummary.AUDIO}</div>
                                <div className='policychat-info-stat-label'>Voice notes</div>
                              </div>
                              <div className='policychat-info-stat'>
                                <div className='policychat-info-stat-value'>{attachmentSummary.FILE}</div>
                                <div className='policychat-info-stat-label'>Documents</div>
                              </div>
                              <div className='policychat-info-stat'>
                                <div className='policychat-info-stat-value'>{messages.length}</div>
                                <div className='policychat-info-stat-label'>Messages</div>
                              </div>
                            </div>
                          </div>

                          {selectedThread.thread_type === 'GROUP' ? (
                            <div className='policychat-info-section'>
                              <div className='policychat-info-section-title'>Participants</div>
                              <div className='policychat-info-participants'>
                                {selectedThread.participants.map((participant) => (
                                  <div key={participant.id} className='policychat-info-participant'>
                                    <UserAvatar
                                      name={participant.user.full_name}
                                      avatar={participant.user.avatar}
                                      colorKey={participant.user.id}
                                      size={40}
                                    />
                                    <div className='min-w-0 flex-grow-1'>
                                      <div className='fw-bold text-truncate'>
                                        {participant.user.full_name}
                                        {participant.is_self ? ' (You)' : ''}
                                      </div>
                                      <div className='text-muted fs-8 text-truncate'>
                                        {participant.user.email}
                                      </div>
                                    </div>
                                    <span className='badge badge-light-primary'>
                                      {participant.role === 'ADMIN' ? 'Admin' : 'Member'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className='policychat-info-section'>
                              <div className='policychat-info-section-title'>Contact</div>
                              <div className='policychat-info-contact-card'>
                                <div className='fw-bold'>
                                  {otherParticipant?.full_name || selectedThread.display_name}
                                </div>
                                <div className='text-muted fs-8'>
                                  {otherParticipant?.email || 'No email available'}
                                </div>
                              </div>
                            </div>
                          )}

                          {sharedMediaItems.length > 0 && (
                            <div className='policychat-info-section'>
                              <div className='policychat-info-section-title'>Recent media</div>
                              <div className='policychat-info-media-grid'>
                                {sharedMediaItems.map((item) => {
                                  const mediaUrl = buildAttachmentUrl(item.attachment, token, true)
                                  const downloadUrl = buildAttachmentUrl(item.attachment, token, false)

                                  return (
                                    <a
                                      key={`${item.messageId}-${item.attachment.id}`}
                                      href={downloadUrl}
                                      target='_blank'
                                      rel='noreferrer'
                                      className='policychat-info-media-tile'
                                      title={formatDateTime(item.createdAt)}
                                    >
                                      {item.attachment.attachment_type === 'VIDEO' ? (
                                        <video src={mediaUrl} muted />
                                      ) : (
                                        <img src={mediaUrl} alt={item.attachment.original_name} />
                                      )}
                                    </a>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {recentAttachmentItems.length > 0 && (
                            <div className='policychat-info-section'>
                              <div className='policychat-info-section-title'>Recent attachments</div>
                              <div className='policychat-info-attachment-list'>
                                {recentAttachmentItems.map((item) => (
                                  <button
                                    key={`${item.messageId}-${item.attachment.id}`}
                                    type='button'
                                    className='policychat-info-attachment'
                                    onClick={() =>
                                      window.open(
                                        buildAttachmentUrl(item.attachment, token, false),
                                        '_blank',
                                        'noopener,noreferrer'
                                      )
                                    }
                                  >
                                    <span className='policychat-info-attachment-icon'>
                                      <i
                                        className={`bi ${
                                          item.attachment.attachment_type === 'AUDIO'
                                            ? 'bi-mic-fill'
                                            : 'bi-paperclip'
                                        }`}
                                      />
                                    </span>
                                    <span className='min-w-0 flex-grow-1'>
                                      <span className='d-block fw-bold text-truncate'>
                                        {item.attachment.original_name}
                                      </span>
                                      <span className='d-block text-muted fs-8 text-truncate'>
                                        {item.senderName} · {formatFileSize(item.attachment.size_bytes)} ·{' '}
                                        {formatDateTime(item.createdAt)}
                                      </span>
                                    </span>
                                    <i className='bi bi-arrow-up-right text-muted' />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </aside>
                    </>
                  )}

                  <div className='border-top policychat-composer-wrap'>
                    {showEmojiTray && (
                      <div className='d-flex flex-wrap gap-2 mb-3'>
                        {QUICK_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type='button'
                            className='btn btn-sm btn-light policychat-emoji-chip'
                            onClick={() => addEmoji(emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}

                    {replyTo && (
                      <div
                        className='d-flex align-items-center justify-content-between rounded px-3 py-2 mb-3'
                        style={{
                          backgroundColor: 'rgba(13, 110, 253, 0.08)',
                          borderLeft: '3px solid #0d6efd',
                        }}
                      >
                        <div className='overflow-hidden'>
                          <div className='fw-bold fs-8 text-primary'>
                            Replying to {replyTo.sender?.full_name || 'User'}
                          </div>
                          <div className='fs-8 text-muted text-truncate' style={{maxWidth: '420px'}}>
                            {replyTo.text ||
                              (replyTo.attachments.length
                                ? getAttachmentLabel(replyTo.attachments[0].attachment_type)
                                : replyTo.message_type)}
                          </div>
                        </div>
                        <button
                          type='button'
                          className='btn btn-sm btn-icon btn-light'
                          onClick={() => setReplyTo(null)}
                        >
                          <i className='bi bi-x-lg fs-7' />
                        </button>
                      </div>
                    )}

                    {draftFiles.length > 0 && (
                      <div className='policychat-draft-preview-grid mb-3'>
                        {draftPreviews.map((preview) => (
                          <div key={preview.key} className='position-relative'>
                            {renderDraftPreview(preview)}
                            <button
                              type='button'
                              className='btn btn-sm btn-icon policychat-draft-remove'
                              onClick={() => removeDraftFile(preview.key)}
                            >
                              <i className='bi bi-x-lg fs-8' />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className='policychat-composer'>
                      <button
                        type='button'
                        className='btn btn-light btn-icon policychat-composer-icon'
                        onClick={() => attachmentInputRef.current?.click()}
                      >
                        <i className='bi bi-plus-lg fs-3' />
                      </button>
                      <button
                        type='button'
                        className='btn btn-light btn-icon policychat-composer-icon'
                        onClick={() => setShowEmojiTray((current) => !current)}
                      >
                        <i className='bi bi-emoji-smile fs-3' />
                      </button>
                      <div className='flex-grow-1'>
                        {isRecording ? (
                          <div className='policychat-voice-recorder'>
                            <div className='policychat-voice-pulse' />
                            <span className='policychat-voice-label'>Recording voice note</span>
                            <span className='policychat-voice-time'>
                              {formatRecordingTime(recordingSeconds)}
                            </span>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-danger ms-auto'
                              onClick={() => void finishRecording(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <textarea
                            ref={composerRef}
                            className='form-control policychat-composer-input'
                            rows={1}
                            placeholder='Type a message'
                            value={draftMessage}
                            onChange={(event) => handleDraftChange(event.target.value)}
                            onKeyDown={handleComposerKeyDown}
                            onPaste={handleComposerPaste}
                          />
                        )}
                      </div>
                      <label className='btn btn-light btn-icon policychat-composer-icon mb-0'>
                        <i className='bi bi-paperclip fs-3' />
                        <input
                          ref={attachmentInputRef}
                          type='file'
                          className='d-none'
                          multiple
                          disabled={isRecording}
                          onChange={handleDraftFiles}
                        />
                      </label>
                      {isRecording ? (
                        <button
                          type='button'
                          className='btn btn-warning policychat-primary-action'
                          onClick={() => void finishRecording(true)}
                        >
                          <i className='bi bi-stop-fill fs-2' />
                        </button>
                      ) : hasComposerPayload ? (
                        <button
                          type='button'
                          className='btn btn-success policychat-primary-action'
                          disabled={sendingMessage}
                          onClick={() => void handleSend()}
                        >
                          <i className={`bi ${sendingMessage ? 'bi-hourglass-split' : 'bi-send-fill'} fs-4`} />
                        </button>
                      ) : (
                        <button
                          type='button'
                          className='btn btn-success policychat-primary-action'
                          onClick={() => void startVoiceRecording()}
                        >
                          <i className='bi bi-mic-fill fs-3' />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className='d-flex flex-column justify-content-center align-items-center h-100 text-center p-10 policychat-empty-state'>
                  <div className='symbol symbol-90px mb-6'>
                    <span className='symbol-label bg-light-primary'>
                      <i className='bi bi-chat-square-heart fs-1 text-primary' />
                    </span>
                  </div>
                  <h3 className='mb-3'>PolicyChat ready</h3>
                  <div className='text-muted mw-400px'>
                    Open a direct chat or create a group to start conversations between PolicyTools users.
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      {previewImage && (
        <ImagePreviewModal
          inlineUrl={previewImage.inlineUrl}
          downloadUrl={previewImage.downloadUrl}
          name={previewImage.name}
          onClose={() => setPreviewImage(null)}
        />
      )}

      {showDirectModal && (
        <UserPickerModal
          loading={loadingUsers}
          onClose={() => setShowDirectModal(false)}
          onCreateDirect={handleCreateDirect}
          onQueryChange={(value) => void handleUserQueryChange(value)}
          query={userQuery}
          users={filteredUsers}
        />
      )}

      {showGroupModal && (
        <GroupModal
          loading={loadingUsers}
          onClose={() => setShowGroupModal(false)}
          onCreateGroup={handleCreateGroup}
          onQueryChange={(value) => void handleUserQueryChange(value)}
          onToggleUser={handleToggleGroupUser}
          query={userQuery}
          selectedUserIds={groupUserIds}
          setTitle={setGroupTitle}
          title={groupTitle}
          users={filteredUsers}
        />
      )}

      {messageMenu && (
        <MessageActionMenu
          x={messageMenu.x}
          y={messageMenu.y}
          canCopy={messageMenu.message.text.trim().length > 0}
          canDownload={messageMenu.message.attachments.length > 0}
          onClose={closeMessageMenu}
          reactions={messageMenu.message.reactions}
          onReact={(emoji) => {
            void handleToggleReaction(messageMenu.message, emoji)
            closeMessageMenu()
          }}
          onReply={() => {
            setReplyTo(messageMenu.message)
            closeMessageMenu()
          }}
          onCopy={() => {
            void handleCopyMessage(messageMenu.message)
            closeMessageMenu()
          }}
          onDownload={() => {
            handleOpenAttachment(messageMenu.message)
            closeMessageMenu()
          }}
        />
      )}

      {contextMenu && (
        <ThreadContextMenu
          thread={contextMenu.thread}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
          onArchive={() =>
            runWithMenuClose(() =>
              archiveThread(contextMenu.thread.id, !contextMenu.thread.is_archived)
            )
          }
          onMute={(duration) =>
            runWithMenuClose(() => muteThread(contextMenu.thread.id, duration))
          }
          onPin={() =>
            runWithMenuClose(() => pinThread(contextMenu.thread.id, !contextMenu.thread.is_pinned))
          }
          onLabel={() => runWithMenuClose(() => handleLabel(contextMenu.thread))}
          onMarkUnread={() => runWithMenuClose(() => markThreadUnread(contextMenu.thread.id))}
          onFavorite={() =>
            runWithMenuClose(() =>
              favoriteThread(contextMenu.thread.id, !contextMenu.thread.is_favorite)
            )
          }
          onBlock={() => runWithMenuClose(() => handleBlock(contextMenu.thread))}
          onClear={() => runWithMenuClose(() => handleClear(contextMenu.thread))}
          onDelete={() => runWithMenuClose(() => handleDelete(contextMenu.thread))}
        />
      )}
    </>
  )
}
